<script>
import devData from './dev-data/weekly_sales_per_group_per_customer.json'
import api from '@/helpers/api'
import { sumBy, chain } from 'lodash'
import moment from 'moment'
import yearRangeMixin from './year-range.mixin'

export default {
  name: 'period-sales-per-customer',
  mixins: [yearRangeMixin],
  props: {
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      selectedCustomer: null,
      isLoading: false,
      total: 0,
      data: []
    }
  },
  watch: {
    startDate: 'onRangeChange',
    endDate: 'onRangeChange',
    year: 'onRangeChange'
  },
  mounted () {
    this.onRefresh()
  },
  methods: {
    onRangeChange () {
      this.selectedCustomer = null
      this.onRefresh()
    },
    selectCustomer (customer) {
      this.selectedCustomer = customer
      this.onRefresh()
    },
    goBack () {
      this.selectedCustomer = null
      this.onRefresh()
    },
    async onRefresh () {
      this.isLoading = true

      var result = []
      if (process.env.NODE_ENV !== 'production') {
        const start = moment(this.startDate)
        const end = moment(this.endDate)
        result = chain(devData)
          .filter(
            x =>
              moment(x.start_date).isSameOrAfter(start) &&
              moment(x.end_date).isSameOrBefore(end)
          )
          .value()
      } else {
        result = await api.stats.weeklySalesPerItemGroupPerCustomer({
          start_date: moment(this.startDate).format('MM/DD/YYYY'),
          end_date: moment(this.endDate).format('MM/DD/YYYY')
        })
      }

      if (this.selectedCustomer !== null) {
        result = result.filter(
          x => x.customer_account_id === this.selectedCustomer.id
        )
        this.data = chain(result)
          .groupBy(x => x.top_item_group.description)
          .map((d, c) => ({
            group: c,
            value: sumBy(d, 'total_sales')
          }))
          .orderBy('value', 'desc')
          .value()
      } else {
        this.data = chain(result)
          .groupBy(x => x.customer_account_id)
          .map((d, c) => ({
            customer: { ...d[0].customer, id: d[0].customer_account_id },
            value: sumBy(d, 'total_sales')
          }))
          .orderBy('value', 'desc')
          .value()
      }

      this.total = sumBy(this.data, 'value')

      this.isLoading = false
    }
  }
}
</script>
<template>
  <div class="portal-breakdown">
    <div class="portal-breakdown__header">
      <div class="portal-breakdown__subtitle">
        <template v-if="selectedCustomer">
          <span>
            {{ $t('Sales') }} {{ selectedCustomer.company_name }}
          </span>
          <button type="button" class="stats-tabs__btn" @click="goBack">
            {{ $t('GO BACK') }}
          </button>
        </template>
        <template v-else>
          {{ $t('Customer Sales') }}
        </template>
      </div>
    </div>

    <div v-if="selectedCustomer === null" class="portal-data-table-wrap">
      <table class="portal-data-table">
        <thead>
          <tr>
            <th>{{ $t('Customer') }}</th>
            <th class="text-right">{{ $t('Sales') }}</th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr>
            <td colspan="2" class="portal-data-table__loading">
              {{ $t('Loading...') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!data.length">
          <tr>
            <td colspan="2" class="portal-data-table__empty">
              {{ $t('dashboard.activity.empty_table') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="row in data"
            :key="row.customer && row.customer.id"
            class="is-clickable"
            @click="selectCustomer(row.customer)"
          >
            <td>
              <a
                href="#"
                class="portal-data-table__primary"
                @click.prevent="selectCustomer(row.customer)"
              >{{ row.customer.company_name }}</a>
            </td>
            <td class="portal-data-table__value">
              {{ row.value | toCurrency }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="portal-data-table-wrap">
      <table class="portal-data-table">
        <thead>
          <tr>
            <th>{{ $t('Category') }}</th>
            <th class="text-right">{{ $t('Sales') }}</th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr>
            <td colspan="2" class="portal-data-table__loading">
              {{ $t('Loading...') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!data.length">
          <tr>
            <td colspan="2" class="portal-data-table__empty">
              {{ $t('dashboard.activity.empty_table') }}
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="(row, index) in data"
            :key="`${row.group}-${index}`"
          >
            <td>
              <span class="portal-data-table__primary">{{ row.group }}</span>
            </td>
            <td class="portal-data-table__value">
              {{ row.value | toCurrency }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="portal-breakdown__footer">
      <span class="font-size-xs text-muted">
        {{ startDate | moment('DD/MM/YYYY') }} -
        {{ endDate | moment('DD/MM/YYYY') }}
      </span>
      <strong>{{ total | toCurrency }}</strong>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.portal-breakdown__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.portal-breakdown__subtitle {
  color: #94a3b8;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.portal-breakdown__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
</style>
